import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { formatUsd } from "@/lib/formatCurrency";
import { useMessageQueue } from "./useMessageQueue";

export function JR2Dialog() {
  const { current, dismiss } = useMessageQueue();
  // The video lives inside Radix's DialogContent portal, which mounts *after*
  // this component's effects run — a plain ref would still be null on the
  // render that opens the dialog. State-as-ref re-runs the effect on attach.
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const [audioBlocked, setAudioBlocked] = useState(false);

  useEffect(() => {
    if (current && videoEl) {
      videoEl.muted = false;
      videoEl.currentTime = 0;
      void videoEl.play().catch(() => {
        // Unmuted autoplay blocked (no user gesture yet) — replay muted so
        // the celebration still shows, and surface an unmute button.
        videoEl.muted = true;
        void videoEl.play().catch(() => {
          /* autoplay fully blocked */
        });
        setAudioBlocked(true);
      });
    } else if (videoEl) {
      videoEl.pause();
      setAudioBlocked(false);
    }
  }, [current, videoEl]);

  const unmute = () => {
    if (videoEl) {
      videoEl.muted = false;
    }
    setAudioBlocked(false);
  };

  return (
    <Dialog open={Boolean(current)} onOpenChange={(open) => !open && dismiss()}>
      <DialogContent className="bg-black/50 border-0 max-w-[90vw] p-2">
        <DialogTitle className="sr-only">New Loan in Process (JR2)!</DialogTitle>
        <div className="relative">
          <video
            ref={setVideoEl}
            src="/images/congrats.mp4"
            className="w-full rounded"
            playsInline
          />
          {audioBlocked && (
            <button
              type="button"
              onClick={unmute}
              className="absolute top-2 right-2 rounded bg-black/70 px-3 py-1 text-sm text-white"
            >
              🔇 Tap for sound
            </button>
          )}
          {current && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1/2 bg-[rgba(214,214,214,0.7)] p-2 rounded text-center">
              <h1 className="font-black text-wallboard-success text-3xl">
                New Loan in Process (JR2)!
              </h1>
              {current.borrowerName && (
                <h1 className="font-black text-wallboard-success text-3xl text-center">
                  {current.borrowerName}
                </h1>
              )}
              {current.loanAmount != null && (
                <h1 className="font-black text-wallboard-success text-3xl text-left">
                  Loan Size: {formatUsd(current.loanAmount)}
                </h1>
              )}
              {current.currentGeqFees != null && (
                <h1 className="font-black text-wallboard-success text-3xl text-left">
                  Net Fees: {formatUsd(current.currentGeqFees)}
                </h1>
              )}
              {current.loanOfficer && (
                <h1 className="font-black text-wallboard-success text-3xl text-left">
                  LO: {current.loanOfficer}
                </h1>
              )}
              {current.juniorLoanOfficer && (
                <h1 className="font-black text-wallboard-success text-3xl text-left">
                  {current.juniorLoanOfficer.includes(",") ? "JR LOs" : "JR LO"}:{" "}
                  {current.juniorLoanOfficer}
                </h1>
              )}
              {current.lsc && (
                <h1 className="font-black text-wallboard-success text-3xl text-left">
                  LSC: {current.lsc}
                </h1>
              )}
              {current.transactionCoordinator && (
                <h1 className="font-black text-wallboard-success text-3xl text-left">
                  TC: {current.transactionCoordinator}
                </h1>
              )}
              {current.salesAssociate && (
                <h1 className="font-black text-wallboard-success text-3xl text-left">
                  SA: {current.salesAssociate}
                </h1>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
